import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { itineraryApi } from '../services/api';
import { Loader2, Plus, Trash2, Wallet, ArrowLeft, PieChart, TrendingUp, DollarSign, UtensilsCrossed, Car, Hotel, Ticket, ShoppingBag, MoreHorizontal, CalendarIcon, Plane, FileText } from 'lucide-react';
import { BudgetComparisonReport } from '../components/BudgetComparisonReport';
import { downloadBudgetPdf } from '../utils/downloadBudgetPdf';

const CATEGORIES = [
  { value: 'food', label: 'Food & Dining', icon: <UtensilsCrossed className="w-4 h-4" />, color: 'bg-orange-500' },
  { value: 'flights', label: 'Flights', icon: <Plane className="w-4 h-4" />, color: 'bg-indigo-500' },
  { value: 'transport', label: 'Local Transport', icon: <Car className="w-4 h-4" />, color: 'bg-blue-500' },
  { value: 'accommodation', label: 'Accommodation', icon: <Hotel className="w-4 h-4" />, color: 'bg-purple-500' },
  { value: 'activities', label: 'Activities', icon: <Ticket className="w-4 h-4" />, color: 'bg-teal-500' },
  { value: 'shopping', label: 'Shopping', icon: <ShoppingBag className="w-4 h-4" />, color: 'bg-pink-500' },
  { value: 'other', label: 'Other', icon: <MoreHorizontal className="w-4 h-4" />, color: 'bg-gray-500' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'INR', 'SGD', 'THB', 'MXN'];

const BudgetTrackerPage = () => {
  const { id: itineraryId } = useParams();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: '', description: '', amount: '', currency: 'USD', date: new Date().toISOString().split('T')[0] });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, sumRes, itinRes] = await Promise.all([
        itineraryApi.listExpenses(itineraryId),
        itineraryApi.getBudgetSummary(itineraryId),
        itineraryApi.get(itineraryId),
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
      setItinerary(itinRes.data);
    } catch (error) {
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [itineraryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddExpense = async () => {
    if (!form.category || !form.description || !form.amount) {
      toast.error('Please fill in all fields');
      return;
    }
    setAdding(true);
    try {
      await itineraryApi.addExpense(itineraryId, { ...form, amount: parseFloat(form.amount) });
      toast.success('Expense added!');
      setForm({ category: '', description: '', amount: '', currency: form.currency, date: new Date().toISOString().split('T')[0] });
      setShowForm(false);
      loadData();
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      await itineraryApi.deleteExpense(expenseId);
      toast.success('Expense deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const getCategoryInfo = (value) => CATEGORIES.find(c => c.value === value) || CATEGORIES[5];
  const totalBudget = itinerary?.trip_input?.budget === 'flexible' ? null : itinerary?.trip_input?.budget === 'medium' ? 2000 : 1000;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50">
        <Header />
        <div className="pt-24 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      </div>
    );
  }

  const tripInput = itinerary?.trip_input || {};
  const cities = tripInput.destination_cities?.length > 0 ? tripInput.destination_cities.join(', ') : tripInput.destination_city || 'Unknown';

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="budget-tracker-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B3C5D] via-[#1a6b5a] to-[#0d4f4f]" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-teal-400/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          <Link to={`/itinerary/${itineraryId}`} className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6" data-testid="back-to-itinerary">
            <ArrowLeft className="w-4 h-4" /> Back to Itinerary
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-3xl text-white drop-shadow-lg flex items-center gap-3">
                <Wallet className="w-8 h-8 text-gold" /> Budget Tracker
              </h1>
              <p className="text-white/70 mt-1">{cities}</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => downloadBudgetPdf(itineraryId, itinerary, expenses, summary)} className="bg-white/20 hover:bg-white/30 text-white font-semibold rounded-full shadow-lg" data-testid="export-budget-pdf">
                <FileText className="w-4 h-4 mr-2" /> Export PDF
              </Button>
              <Button onClick={() => setShowForm(!showForm)} className="bg-gradient-to-r from-gold to-amber-400 text-ocean font-semibold rounded-full shadow-lg hover:shadow-xl" data-testid="add-expense-toggle">
                <Plus className="w-4 h-4 mr-2" /> Add Expense
              </Button>
            </div>
          </motion.div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-lg border border-white/30" data-testid="total-spent-card">
              <div className="flex items-center gap-2 text-ocean/70 text-sm mb-1"><DollarSign className="w-4 h-4" />Total Spent</div>
              <p className="text-2xl font-bold text-gray-900">${summary?.total_spent?.toFixed(2) || '0.00'}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-lg border border-white/30" data-testid="expense-count-card">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1"><PieChart className="w-4 h-4" />Expenses</div>
              <p className="text-2xl font-bold text-gray-900">{summary?.expense_count || 0}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-lg border border-white/30" data-testid="daily-avg-card">
              <div className="flex items-center gap-2 text-ocean/70 text-sm mb-1"><TrendingUp className="w-4 h-4" />Daily Avg</div>
              <p className="text-2xl font-bold text-gray-900">
                ${summary?.expense_count > 0 ? (summary.total_spent / Math.max(1, new Set(expenses.map(e => e.date)).size)).toFixed(2) : '0.00'}
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-lg border border-white/30">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1"><Wallet className="w-4 h-4" />Top Category</div>
              <p className="text-lg font-bold text-gray-900 capitalize">
                {summary?.by_category ? Object.entries(summary.by_category).sort((a, b) => b[1] - a[1])[0]?.[0]?.replace(/_/g, ' ') || '-' : '-'}
              </p>
            </motion.div>
          </div>

          {/* Budget Comparison Report */}
          <BudgetComparisonReport itineraryId={itineraryId} />

          {/* Category Breakdown */}
          {summary?.by_category && Object.keys(summary.by_category).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/30 mb-8" data-testid="category-breakdown">
              <h3 className="font-semibold text-gray-900 mb-4">Spending by Category</h3>
              <div className="space-y-3">
                {Object.entries(summary.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => {
                  const info = getCategoryInfo(cat);
                  const percent = summary.total_spent > 0 ? (amount / summary.total_spent) * 100 : 0;
                  return (
                    <div key={cat} className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${info.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>{info.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-700 capitalize">{cat.replace(/_/g, ' ')}</span>
                          <span className="font-medium">${amount.toFixed(2)} ({percent.toFixed(0)}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className={`${info.color} h-2 rounded-full transition-all duration-500`} style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Add Expense Form */}
          <AnimatePresence>
            {showForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-6 mb-8 overflow-hidden" data-testid="add-expense-form">
                <h3 className="font-semibold text-gray-900 mb-4">Add New Expense</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600 text-sm">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                      <SelectTrigger className="mt-1 h-11" data-testid="expense-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            <span className="flex items-center gap-2">{c.icon} {c.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-600 text-sm">Description</Label>
                    <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="e.g., Lunch at local cafe" className="mt-1 h-11" data-testid="expense-description-input" />
                  </div>
                  <div>
                    <Label className="text-gray-600 text-sm">Amount</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={form.currency} onValueChange={(v) => setForm(p => ({ ...p, currency: v }))}>
                        <SelectTrigger className="w-24 h-11" data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
                        placeholder="0.00" className="flex-1 h-11" data-testid="expense-amount-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-600 text-sm">Date</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input type="text"
                        value={form._dateDisplay !== undefined ? form._dateDisplay : (form.date ? form.date.split('-').reverse().join('/') : '')}
                        onChange={(e) => {
                          let v = e.target.value.replace(/[^0-9/]/g, '').slice(0, 10);
                          setForm(p => ({ ...p, _dateDisplay: v }));
                          const parts = v.split('/');
                          if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
                            const d = parseInt(parts[0]), m = parseInt(parts[1]), y = parseInt(parts[2]);
                            if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 2000) {
                              setForm(p => ({ ...p, date: `${parts[2]}-${parts[1]}-${parts[0]}`, _dateDisplay: v }));
                            }
                          }
                        }}
                        onBlur={() => setForm(p => { const { _dateDisplay, ...rest } = p; return rest; })}
                        placeholder="dd/mm/yyyy"
                        className="h-11 w-auto flex-shrink-0" style={{width: '130px'}} data-testid="expense-date-input" />
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="text-gray-400 hover:text-gray-600 flex-shrink-0" data-testid="expense-date-calendar-btn">
                            <CalendarIcon className="h-5 w-5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={form.date ? new Date(form.date + 'T00:00:00') : undefined}
                            onSelect={(date) => {
                              if (date) {
                                const y = date.getFullYear();
                                const m = String(date.getMonth() + 1).padStart(2, '0');
                                const d = String(date.getDate()).padStart(2, '0');
                                setForm(p => { const { _dateDisplay, ...rest } = p; return { ...rest, date: `${y}-${m}-${d}` }; });
                              }
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                  <Button variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-expense-btn">Cancel</Button>
                  <Button onClick={handleAddExpense} disabled={adding}
                    className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full" data-testid="save-expense-btn">
                    {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    {adding ? 'Adding...' : 'Add Expense'}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Expense List */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 overflow-hidden" data-testid="expense-list">
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Expense History</h3>
            </div>
            {expenses.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No expenses yet. Start tracking your spending!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {expenses.map((expense, idx) => {
                  const info = getCategoryInfo(expense.category);
                  return (
                    <motion.div key={expense.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors" data-testid={`expense-item-${idx}`}>
                      <div className={`w-10 h-10 ${info.color} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                        {info.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{expense.description}</p>
                        <p className="text-xs text-gray-500">{expense.date ? expense.date.split('-').reverse().join('/') : ''} - {info.label}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-gray-900">{expense.currency} {expense.amount.toFixed(2)}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(expense.id)}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0" data-testid={`delete-expense-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom Add Expense Button */}
          <div className="flex justify-center mt-6 mb-4">
            <Button
              onClick={() => { setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="bg-gradient-to-r from-gold to-amber-400 text-ocean font-semibold rounded-full shadow-lg hover:shadow-xl px-8 py-3"
              data-testid="add-expense-bottom"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Expense
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BudgetTrackerPage;
